/**
 * Manual fallback for Meta's voice verification call.
 *
 * Normally the backend answers the call and reads the code out of the
 * transcript. That fails when the number is not hosted on our Twilio account,
 * or when Meta speaks the code in a language the speech gather mistranscribes.
 * Arming a redirect forwards the next verification call to a phone a human
 * answers, so the code can be typed into the field below.
 */
import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/base/buttons'
import {
  useRedirectStatus,
  useActivateRedirect,
  useDeactivateRedirect,
} from '@/hooks/api/useWhatsAppSignup'

const BASE = 'resources.assistants.whatsapp_signup'

function formatCountdown(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${String(seconds).padStart(2, '0')}`
}

export function VerificationRedirectCard({ assistantId }: { assistantId: number }) {
  const { t } = useTranslation()
  const [targetNumber, setTargetNumber] = useState('')

  const { data: redirect } = useRedirectStatus(assistantId)
  const activate = useActivateRedirect()
  const deactivate = useDeactivateRedirect()

  const isActive = !!redirect?.active

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-900">
      <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
        {t(`${BASE}.redirect.title`)}
      </h3>
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
        {t(`${BASE}.redirect.description`)}
      </p>

      {isActive ? (
        <div className="mt-4 space-y-3">
          <p className="text-sm text-gray-900 dark:text-gray-100">
            {t(`${BASE}.redirect.active`, {
              number: redirect?.redirect_target ?? '',
              countdown: formatCountdown(redirect?.remaining_seconds ?? 0),
            })}
          </p>
          <Button
            type="button"
            color="secondary"
            size="sm"
            isLoading={deactivate.isPending}
            onPress={async () => {
              try {
                await deactivate.mutateAsync({ assistant_id: assistantId })
                toast.success(t(`${BASE}.redirect.toast_deactivated`))
              } catch {
                toast.error(t(`${BASE}.redirect.toast_failed`))
              }
            }}
          >
            {t(`${BASE}.redirect.deactivate`)}
          </Button>
        </div>
      ) : (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <input
            type="tel"
            inputMode="tel"
            value={targetNumber}
            onChange={(event) => setTargetNumber(event.target.value)}
            placeholder={t(`${BASE}.redirect.placeholder`)}
            aria-label={t(`${BASE}.redirect.placeholder`)}
            className="w-56 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
          />
          <Button
            type="button"
            color="primary"
            size="sm"
            className="bg-[#222222] hover:bg-[#333333] dark:bg-gray-100 dark:hover:bg-white"
            isDisabled={!targetNumber.trim() || activate.isPending}
            isLoading={activate.isPending}
            onPress={async () => {
              try {
                await activate.mutateAsync({
                  assistant_id: assistantId,
                  redirect_phone_number: targetNumber.trim(),
                })
                toast.success(t(`${BASE}.redirect.toast_activated`))
              } catch {
                toast.error(t(`${BASE}.redirect.toast_failed`))
              }
            }}
          >
            {t(`${BASE}.redirect.activate`)}
          </Button>
        </div>
      )}
    </div>
  )
}
